// src/pages/ApplicationBannerHome/index.tsx
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Text, Flex, useToast, Skeleton, Switch, Button } from '@chakra-ui/react';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { breadcrumb } from './breadcrumb';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { StoreFilterBox } from './components/BannerFilterBox';
import { GetParamsObject } from '@/utils';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { ApplicationComponentId } from '@/model/application-component';
import { useAppApplicationComponentArea, useEnableApplicationComponentMutation } from '@/services/queries/admin-config';
import { Banner } from '@/model';
import { Filters } from './types';
import { useBanner, useDeleteBannerMutation, useGetBannerByUUID } from '@/services/queries/banner';
import { BannerTable } from './components/BannerTable/BannerTable';
import { BannerCreateModal } from './components/BannerCreateModal';
import { BannerUpdateModal } from './components/BannerEditModal';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAllClub } from '@/services/queries/club';

export const ApplicationBanner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const [isAppAreaActive, setIsAppAreaActive] = useState(false);

  const { data: appAreaData, isLoading: isLoadingAppArea } = useAppApplicationComponentArea(ApplicationComponentId.BANNER);
  const { mutate: enableAppAreaMutation, isLoading: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPendingAppAreaState(isChecked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const SucessactionString = pendingAppAreaState
      ? t('pages.application_banner.banner_area_toggled_enable')
      : t('pages.application_banner.banner_area_toggled_disable');
    const errorActionString = pendingAppAreaState
      ? t('pages.application_banner.banner_area_toggle_failed_enable')
      : t('pages.application_banner.banner_area_toggle_failed_disable');

    setIsAppAreaActive(pendingAppAreaState);

    enableAppAreaMutation([{ component_id: ApplicationComponentId.BANNER, active: pendingAppAreaState }], {
      onSuccess: () => {
        toast({
          status: 'success',
          title: SucessactionString,
        });
      },
      onError: () => {
        toast({
          status: 'error',
          title: errorActionString,
        });

        setIsAppAreaActive(!pendingAppAreaState);
      },
    });

    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const [selectedItem, setSelectedItem] = useState<Banner | undefined>(undefined);
  const [isShowEditModal, setIsShowEditModal] = useState<boolean>(false);
  const [isShowCreateModal, setIsShowCreateModal] = useState<boolean>(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);

  const filtersForm = useFiltersForm<Filters>({
    defaultValues: {
      team_uuid: '',
      title: '',
      start_date: '',
      end_date: '',
      partner_uuid: '',
    },
  });

  const filterData = filtersForm.watch();

  const [sorting, setSorting] = useState<{ field: string; desc: boolean }>({ field: 'created_at', desc: true });
  const [pagination, setPagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 30,
  });

  const params = GetParamsObject(
    filterData,
    pagination.pageNumber,
    pagination.pageSize,
    sorting.desc ? 'desc' : 'asc',
    sorting.field,
  );

  const { data: clubs = [] } = useAllClub(true);
  const { data: bannersData, isLoading: isLoadingBannerData, forceRefetch } = useBanner(params);

  const banners = bannersData?.data || [];

  const getBannerByUUID = useGetBannerByUUID();
  const deleteBannerMutation = useDeleteBannerMutation();

  const onHandleEdit = useCallback(
    (item: Banner) => {
      getBannerByUUID.mutate(item.uuid, {
        onSuccess: res => {
          setSelectedItem(res);
          setIsShowEditModal(true);
        },
        onError: (resp: any) => {
          setSelectedItem(undefined);
          setIsShowEditModal(false);
          toast({
            status: 'error',
            title: (resp && resp.message) || t('pages.application_banner.banner_not_found'),
          });
        },
      });
    },
    [getBannerByUUID, toast, t],
  );

  const onHandleDelete = useCallback((item: Banner) => {
    setSelectedItem(item);
    setIsConfirmModalOpen(true);
  }, []);

  const onHandleEditDone = useCallback(() => {
    setIsShowEditModal(false);
    setSelectedItem(undefined);
    forceRefetch();
  }, [forceRefetch]);

  const onHandleDeleteConfirm = useCallback(() => {
    if (!selectedItem?.uuid) return;

    setIsConfirmModalOpen(false);

    deleteBannerMutation.mutate(selectedItem.uuid, {
      onSuccess: () => {
        toast({
          title: t('pages.application_banner.banner_delete_success'),
          status: 'success',
        });

        forceRefetch();
        setSelectedItem(undefined);
      },
      onError: (resp: any) => {
        setSelectedItem(undefined);
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_banner.banner_delete_failed'),
        });
      },
    });
  }, [deleteBannerMutation, forceRefetch, selectedItem, toast, t]);

  const onHandleCreateBannerDone = useCallback(() => {
    setIsShowCreateModal(false);
    forceRefetch();
  }, [forceRefetch]);

  useEffect(() => {
    if (!appAreaData) return;

    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  useEffect(() => {
    if (location && location.state && location.state.banner_uuid) {
      onHandleEdit({ uuid: location.state.banner_uuid } as Banner);
      navigate(location.pathname, { state: { ...location.state, banner_uuid: undefined } });
    }

    if (location && location.state && location.state.new_banner) {
      setIsShowCreateModal(true);
      navigate(location.pathname, { state: { ...location.state, new_banner: undefined } });
    }
  }, [location, navigate, onHandleEdit]);

  return (
    <PageContent title={t('pages.application_banner.banner_title')} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex direction={{ base: 'column', md: 'row' }} justifyContent={{ md: 'space-between' }} gap={4}>
          <Flex alignItems='center' gap={4}>
            {isLoadingAppArea ? (
              <>
                <Skeleton w='130px' h='21px' />
                <Skeleton w='102px' h='21px' />
              </>
            ) : (
              <>
                <Text fontWeight='bold' color='brand.500'>
                  {t('pages.application_banner.banner_title_text')}
                </Text>

                <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                  {isAppAreaActive ? t('pages.application_banner.banner_enabled') : t('pages.application_banner.banner_disabled')}
                </Switch>
              </>
            )}
          </Flex>

          <Flex gap={2} direction={{ base: 'column', md: 'row' }}>
            <Button
              display='flex'
              alignItems='center'
              gap={2}
              onClick={() => setIsShowCreateModal(true)}
              w={{ base: '100%', md: 'initial' }}
            >
              <ChakraFeatherIcon name='plus-circle' />
              {t('pages.application_banner.banner_add_banner')}
            </Button>
          </Flex>
        </Flex>

        <Text fontSize='sm' color='gray.600'>
          {t('pages.application_banner.banner_page_description')}
        </Text>

        <SectionBox bodyProps={{ gap: 'small' }}>
          <StoreFilterBox clubs={clubs} filtersForm={filtersForm} />
        </SectionBox>

        <Flex direction='column' gap='deka' mt={8}>
          <Flex direction='column' gap='micro'>
            <BannerTable
              data={banners}
              isLoading={isLoadingBannerData}
              sorting={sorting}
              pageNumber={pagination.pageNumber}
              pageSize={pagination.pageSize}
              totalPages={bannersData?.meta.pages || 0}
              totalItems={bannersData?.meta.total || 0}
              onSortingChange={(field, desc) => setSorting({ field, desc })}
              onPaginationChange={(pageNumber, pageSize) => setPagination({ pageNumber, pageSize })}
              OnDelete={item => onHandleDelete(item)}
              OnEdit={item => onHandleEdit(item)}
            />
          </Flex>
        </Flex>
      </SectionBox>

      <BannerCreateModal
        isOpen={isShowCreateModal}
        onClose={() => setIsShowCreateModal(false)}
        onDone={() => onHandleCreateBannerDone()}
      />

      {selectedItem && (
        <BannerUpdateModal
          isOpen={!!isShowEditModal}
          onClose={() => {
            setIsShowEditModal(false);
            setSelectedItem(undefined);
          }}
          item={selectedItem}
          onDone={onHandleEditDone}
        />
      )}

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedItem(undefined);
        }}
        onConfirm={onHandleDeleteConfirm}
        isLoading={false}
        title={t('pages.application_banner.banner_delete_title')}
        description={<Text>{t('pages.application_banner.banner_delete_description', { title: selectedItem?.title })}</Text>}
      />

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t('pages.application_banner.banner_title_text')}
        description={
          <Text>
            {pendingAppAreaState
              ? t('pages.application_banner.banner_description_enabled')
              : t('pages.application_banner.banner_description_disabled')}
          </Text>
        }
      />

      <CenteredSpinner
        isLoading={isLoadingEnableOrDisableAppArea || getBannerByUUID.isLoading || deleteBannerMutation.isLoading}
      />
    </PageContent>
  );
};
